/**
 * The three questions the feedback panel asks instead of one free "Description" (Sofia, Oct 20
 * 2026). "What's wrong" alone left Pia guessing the goal and asking for it in the thread; a goal
 * without one concrete case cannot be reproduced. The proxy posts the three answers as one
 * labelled description (proxy-server/lib/feedbackMessage.js `composeDescription`), so the board
 * and Pia read them the way they read a description before.
 */
import { Textarea } from '@finn-auto/design-system/atoms/textarea';
import type { FeedbackType } from '../services/feedbackApi';

/** Matches MAX_DESCRIPTION_PART in the proxy: three answers plus labels fit one Slack section. */
const MAX_PART = 900;

/** The first prompt follows the type: a feature has no "what happened". */
const PROBLEM_PLACEHOLDER: Record<FeedbackType, string> = {
  bug: 'What happened, where, and since when? Steps to reproduce.',
  feature: 'What is missing today, and what does it cost you?',
  improvement: 'What works today, but not well enough?',
  other: 'What is this about?',
};

export function DescriptionFields({
  type,
  disabled,
  problem,
  expected,
  example,
  onProblem,
  onExpected,
  onExample,
}: {
  type: FeedbackType | null;
  disabled: boolean;
  problem: string;
  expected: string;
  example: string;
  onProblem: (value: string) => void;
  onExpected: (value: string) => void;
  onExample: (value: string) => void;
}) {
  return (
    <>
      <Textarea
        label="What's the problem? *"
        rows={4}
        value={problem}
        disabled={disabled}
        maxLength={MAX_PART}
        placeholder={PROBLEM_PLACEHOLDER[type ?? 'bug']}
        onChange={(e) => onProblem(e.target.value)}
      />

      <Textarea
        label="What should happen instead? *"
        rows={3}
        value={expected}
        disabled={disabled}
        maxLength={MAX_PART}
        placeholder="Describe the result you want — what you would see or be able to do."
        onChange={(e) => onExpected(e.target.value)}
      />

      <Textarea
        label="Give us an example *"
        rows={3}
        value={example}
        disabled={disabled}
        maxLength={MAX_PART}
        placeholder="One concrete case: a candidate, role, interview link or date where this happens."
        onChange={(e) => onExample(e.target.value)}
      />
    </>
  );
}
